import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AvaturnSDK,
  AssetItem,
  BodyItem,
  EyeColorType,
  ColorValueHex,
  EditableBodyProportions,
} from '@avaturn/sdk';
import * as Sentry from '@sentry/react';
import { useCreateExportMutation } from '~/features/avaturn';
import { SECOND } from '~/constants';
import useCreateNewCharacterStore from '~/store/useCreateNewCharacterStore';
import useToast from '~/store/useToast';
import { FilterAvaturnProps } from './FilterAvaturn';
import { AvarturnIFrameName } from './FilterAvaturn.styled';

type AssetType = 'eyewear' | 'hair' | 'body' | 'clothes' | 'footwear' | 'etc';
const assetTypeArray: AssetType[] = [
  'eyewear',
  'hair',
  'body',
  'clothes',
  'footwear',
  'etc',
];
const disableUi = true;
export type AvaturnAssetList = Record<
  Exclude<AssetType, 'body' | 'etc'>,
  AssetItem[]
>;
const initialAssetList: AvaturnAssetList = {
  eyewear: [],
  hair: [],
  clothes: [],
  footwear: [],
};
interface useFilterAvaturnProps {
  onNextStep: FilterAvaturnProps['onNextStep'];
}
const useFilterAvaturn = ({ onNextStep }: useFilterAvaturnProps) => {
  const { sessionUrl, avaturnId } = useCreateNewCharacterStore((s) => ({
    sessionUrl: s.customizeAvaturn.avarturn.url,
    avaturnId: s.customizeAvaturn.avarturn.id,
  }));
  const setAvaturnFinalState = useCreateNewCharacterStore(
    (s) => s.setAvaturnFinalState,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [sdk] = useState<AvaturnSDK>(new AvaturnSDK());
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [bodyList, setBodyList] = useState<BodyItem[]>([]);
  const [assetType, setAssetType] = useState<AssetType>();
  const [assetList, setAssetList] = useState(initialAssetList);
  const { showToast } = useToast();
  const { mutate } = useCreateExportMutation();
  const [isPending, setIsPending] = useState(false);
  useEffect(() => {
    if (!containerRef.current) return;
    setEditorLoaded(false);
    sdk
      .init(containerRef.current, {
        url: sessionUrl,
        iframeClassName: AvarturnIFrameName,
        disableUi,
      })
      .then(() => {
        sdk.on('load', () => {
          setEditorLoaded(true);
          sdk.getBodyList().then(setBodyList);
          sdk.getAssetList().then((list) => {
            const asset = list.reduce(
              (acc, curr) => {
                if (curr.category === 'eyewear') {
                  acc.eyewear.push(curr);
                }
                if (curr.category === 'hairAndHeadwear') {
                  acc.hair.push(curr);
                }
                if (curr.category === 'clothing') {
                  acc.clothes.push(curr);
                }
                if (curr.category === 'footwear') {
                  acc.footwear.push(curr);
                }
                return acc;
              },
              {
                eyewear: [],
                hair: [],
                clothes: [],
                footwear: [],
              } as AvaturnAssetList,
            );
            setAssetList(asset);
            setAssetType(assetTypeArray[0]);
            sdk.setBodyProportions('head', -5.0);
          });
        });
      })
      .catch((reason) => {
        Sentry.captureException(reason);
        sdk.getBodyList().then(setBodyList);
        sdk.getAssetList().then((list) => {
          const asset = list.reduce(
            (acc, curr) => {
              if (curr.category === 'eyewear') {
                acc.eyewear.push(curr);
              }
              if (curr.category === 'hairAndHeadwear') {
                acc.hair.push(curr);
              }
              if (curr.category === 'clothing') {
                acc.clothes.push(curr);
              }
              if (curr.category === 'footwear') {
                acc.footwear.push(curr);
              }
              return acc;
            },
            {
              eyewear: [],
              hair: [],
              clothes: [],
              footwear: [],
            } as AvaturnAssetList,
          );
          setAssetList(asset);
          setAssetType(assetTypeArray[0]);
          sdk.setBodyProportions('head', -5.0);
        });
      });
    return () => {
      setAssetList({
        eyewear: [],
        hair: [],
        clothes: [],
        footwear: [],
      });
      setBodyList([]);
      setAssetType(undefined);
      sdk.destroy();
    };
  }, [sdk, sessionUrl]);
  const handleSetAsset = useCallback(
    async (id: string) => {
      await sdk.setActiveAsset(id);
    },
    [sdk],
  );
  const handleSetBody = useCallback(
    async (id: string) => {
      await sdk.setActiveBody(id);
    },
    [sdk],
  );
  const handleChangeHairColor = useCallback(
    (color: string, setColor: (color: string) => void) => {
      sdk.setHairColor(color as `#${string}`);
      setColor(color);
    },
    [sdk],
  );
  const handleChangeSkinToneColor = useCallback(
    (value: number, setValue: (value: number) => void) => {
      sdk.setSkinToneCorrection(value);
      setValue(value);
    },
    [sdk],
  );
  const handleChangeHeadProportions = useCallback(
    (value: number, setValue: (value: number) => void) => {
      sdk.setBodyProportions('head', value);
      setValue(value);
    },
    [sdk],
  );
  const handleChangeArmsProportions = useCallback(
    (value: number, setValue: (value: number) => void) => {
      sdk.setBodyProportions('arms', value);
      setValue(value);
    },
    [sdk],
  );
  const handleChangeChestProportions = useCallback(
    (value: number, setValue: (value: number) => void) => {
      sdk.setBodyProportions('chest', value);
      setValue(value);
    },
    [sdk],
  );
  const handleChangeEyeColor = useCallback(
    (color: EyeColorType, setColor: (color: EyeColorType) => void) => {
      sdk.setEyeColor(color);
      setColor(color);
    },
    [sdk],
  );
  const handleChangeEyeWearColor = useCallback(
    (
      color: ColorValueHex | 'transparent',
      setColor: (color: ColorValueHex | 'transparent') => void,
    ) => {
      console.log(color);
      sdk.setEyewearColor(color);
      setColor(color);
    },
    [sdk],
  );
  const handleFinishClick = async () => {
    setIsPending(true);
    mutate(
      {
        avatar_id: avaturnId,
      },
      {
        onSuccess: ({ data, status }) => {
          if (data.status === 'ready') {
            setAvaturnFinalState({
              characterId: avaturnId,
              uploadUrl: '',
            });
            onNextStep();
            setIsPending(false);
            return;
          }
          if (data.status === 'failed') {
            setIsPending(false);
            showToast('error', 'export avatar error');
            return;
          }
          if (data.status === 'pending') {
            setTimeout(() => {
              handleFinishClick();
            }, 1.2 * SECOND);
            return;
          }
        },
      },
    );
  };
  return {
    isPending,
    containerRef,
    disableUi,
    sdk,
    editorLoaded,
    assetType,
    assetList,
    bodyList,
    assetTypeArray,
    handleFinishClick,
    handleSetAsset,
    handleSetBody,
    setAssetType,
    handleChangeHairColor,
    handleChangeSkinToneColor,
    handleChangeEyeColor,
    handleChangeEyeWearColor,
    handleChangeHeadProportions,
    handleChangeArmsProportions,
    handleChangeChestProportions,
  };
};
export default useFilterAvaturn;
